import {
  HttpTransportType,
  HubConnectionBuilder,
  LogLevel,
  type IRetryPolicy,
  type RetryContext,
} from '@microsoft/signalr';
import type { TokenManager } from './tokens';
import type { AccountValuation, HubEvent, HubQuote, HubState, Resync } from './hubTypes';

export type { AccountValuation, HubEvent, HubQuote, HubState, Resync } from './hubTypes';

/** The part of a SignalR connection this client uses; tests supply a fake. */
export interface HubConnectionLike {
  start(): Promise<void>;
  stop(): Promise<void>;
  invoke<T = unknown>(method: string, ...args: unknown[]): Promise<T>;
  on(method: string, handler: (...args: never[]) => void): void;
  onreconnecting(handler: () => void): void;
  onreconnected(handler: () => void): void;
  onclose(handler: () => void): void;
}

export interface ConnectionOptions {
  accessTokenFactory: () => Promise<string>;
  retryPolicy: IRetryPolicy;
}

export interface HubHandlers {
  quotes: (quotes: HubQuote[]) => void;
  account: (valuation: AccountValuation) => void;
  event: (event: HubEvent) => void;
  /** Staff only (after {@link HubClient.watchStaffAlerts}): a margin call, stop out or challenge event of any account. */
  staffAlert: (event: HubEvent) => void;
  feed: (up: boolean) => void;
  resync: (resync: Resync) => void;
  state: (state: HubState) => void;
}

type SessionLike = Pick<TokenManager, 'getToken' | 'refresh' | 'session'>;

export interface HubClientOptions {
  tokens: SessionLike;
  url?: string;
  createConnection?: (options: ConnectionOptions) => HubConnectionLike;
  /** Delays between reconnect attempts; the last one repeats forever. */
  backoffMs?: number[];
  random?: () => number;
}

interface Watch {
  /** The seq up to which this client has seen every event of the account. Null until the server told us. */
  lastSeq: number | null;
}

const DEFAULT_BACKOFF = [0, 1_000, 2_000, 4_000, 8_000, 15_000];

function realConnection(url: string, options: ConnectionOptions): HubConnectionLike {
  return new HubConnectionBuilder()
    .withUrl(url, {
      accessTokenFactory: options.accessTokenFactory, // called again for every (re)connect, so a renewed token is used
      transport: HttpTransportType.WebSockets,
      skipNegotiation: true,
    })
    .withAutomaticReconnect(options.retryPolicy)
    .configureLogging(LogLevel.Warning)
    .build() as unknown as HubConnectionLike;
}

/**
 * The live connection to `/hubs/trading`.
 *
 * It reconnects forever with backoff (as long as the user is signed in), renews the access token for every attempt, and after
 * a reconnect puts the subscriptions back. For each watched account it remembers the last event `seq` it saw, so the server
 * replays exactly what was missed; events at or below that `seq` are dropped, so a replay overlapping live delivery never
 * shows an event twice. When the server says the client must reload (`resync`), that is reported only after the subscriptions
 * are back, so the reload starts from a consistent baseline.
 */
export class HubClient {
  private readonly tokens: SessionLike;
  private readonly connection: HubConnectionLike;
  private readonly backoff: number[];
  private readonly random: () => number;
  private readonly handlers: { [K in keyof HubHandlers]: Set<HubHandlers[K]> } = {
    quotes: new Set(),
    account: new Set(),
    event: new Set(),
    staffAlert: new Set(),
    feed: new Set(),
    resync: new Set(),
    state: new Set(),
  };
  private readonly symbols = new Set<string>();
  private readonly watched = new Map<number, Watch>();
  private readonly pendingResync = new Map<number | null, string>();
  private readonly watchWaiters = new Map<number, (() => void)[]>();
  private current: HubState = 'disconnected';
  private stopped = true;
  private resubscribing = false;
  private staffAlerts = false;
  private verifiedThisOutage = false;
  private startLoop: Promise<void> | null = null;

  constructor(options: HubClientOptions) {
    this.tokens = options.tokens;
    this.backoff = options.backoffMs ?? DEFAULT_BACKOFF;
    this.random = options.random ?? Math.random;
    const url = options.url ?? '/hubs/trading';
    const connectionOptions: ConnectionOptions = {
      accessTokenFactory: async () => (await this.tokens.getToken()) ?? '',
      retryPolicy: { nextRetryDelayInMilliseconds: (context) => this.nextDelay(context) },
    };
    this.connection = (options.createConnection ?? ((o) => realConnection(url, o)))(connectionOptions);

    this.connection.on('quotes', ((quotes: HubQuote[]) => this.emit('quotes', quotes)) as never);
    this.connection.on('account', ((valuation: AccountValuation) =>
      this.emit('account', valuation)) as never);
    this.connection.on('feed', ((message: { up: boolean }) => this.emit('feed', message.up)) as never);
    this.connection.on('event', ((event: HubEvent) => this.onEvent(event)) as never);
    this.connection.on('resync', ((resync: Resync) => void this.onResync(resync)) as never);
    this.connection.onreconnecting(() => {
      this.setState('reconnecting');
      void this.verifySession();
    });
    this.connection.onreconnected(() => void this.onConnected());
    this.connection.onclose(() => this.setState('disconnected'));
  }

  get state(): HubState {
    return this.current;
  }

  on<K extends keyof HubHandlers>(name: K, handler: HubHandlers[K]): () => void {
    this.handlers[name].add(handler);
    return () => this.handlers[name].delete(handler);
  }

  /** Connects, retrying until it works or `stop()` is called. */
  start(): Promise<void> {
    if (!this.stopped) return this.startLoop ?? Promise.resolve();
    this.stopped = false;
    this.startLoop = this.connectLoop();
    return this.startLoop;
  }

  async stop(): Promise<void> {
    this.stopped = true;
    this.setState('disconnected');
    await this.connection.stop();
  }

  /** Adds symbols to the quote stream; the current quote of each arrives immediately. */
  async subscribeQuotes(symbols: string[]): Promise<void> {
    const fresh = symbols.filter((s) => !this.symbols.has(s));
    fresh.forEach((s) => this.symbols.add(s));
    if (fresh.length > 0 && this.current === 'connected') {
      await this.connection.invoke('SubscribeQuotes', fresh);
    }
  }

  async unsubscribeQuotes(symbols: string[]): Promise<void> {
    symbols.forEach((s) => this.symbols.delete(s));
    if (this.current === 'connected') {
      await this.connection.invoke('UnsubscribeQuotes', symbols);
    }
  }

  /**
   * Starts streaming an account. Resolves once the server confirmed (so a snapshot fetched afterwards cannot miss events),
   * with the event `seq` baseline. Waits for the connection when there is none yet.
   */
  async watchAccount(login: number): Promise<void> {
    if (!this.watched.has(login)) this.watched.set(login, { lastSeq: null });
    if (this.current === 'connected') {
      await this.watch(login);
      return;
    }
    // No connection yet: the reconnect logic watches the account as soon as it is up. Resolve only then, so a snapshot
    // fetched after this call cannot precede the subscription (and miss events in between).
    await new Promise<void>((resolve) => {
      const list = this.watchWaiters.get(login) ?? [];
      list.push(resolve);
      this.watchWaiters.set(login, list);
    });
  }

  /** Staff: asks for every account's margin calls, stop outs and challenge events (kept across reconnects). */
  async watchStaffAlerts(): Promise<void> {
    this.staffAlerts = true;
    if (this.current === 'connected') await this.connection.invoke('WatchStaffAlerts');
  }

  async unwatchAccount(login: number): Promise<void> {
    this.watched.delete(login);
    this.watchWaiters.get(login)?.forEach((resolve) => resolve()); // nobody should wait for an account we no longer watch
    this.watchWaiters.delete(login);
    if (this.current === 'connected') {
      await this.connection.invoke('UnwatchAccount', login);
    }
  }

  // ---------------------------------------------------------------- internals
  private async connectLoop(): Promise<void> {
    this.setState('connecting');
    for (let attempt = 0; !this.stopped; attempt++) {
      if (this.tokens.session.status === 'anonymous' || this.tokens.session.status === 'expired') {
        this.setState('disconnected');
        return;
      }
      try {
        await this.connection.start();
        await this.onConnected();
        return;
      } catch {
        this.setState('connecting');
        await new Promise((resolve) => setTimeout(resolve, this.delayFor(attempt)));
      }
    }
  }

  /**
   * The server may have dropped us because the session was revoked, without the client ever seeing why (the goodbye message
   * can be lost). Reconnecting with a token that still looks valid would then loop forever, so once per outage the session is
   * checked with the server: a refused renewal ends the session and the connection.
   */
  private async verifySession(): Promise<void> {
    if (this.verifiedThisOutage || this.stopped) return;
    this.verifiedThisOutage = true;
    const rejected = await this.tokens.getToken();
    if (
      rejected &&
      !(await this.tokens.refresh(rejected)) &&
      this.tokens.session.status !== 'authenticated'
    ) {
      await this.stop();
    }
  }

  private async onConnected(): Promise<void> {
    this.verifiedThisOutage = false;
    this.setState('connected');
    await this.resubscribe();
  }

  private async resubscribe(): Promise<void> {
    this.resubscribing = true;
    try {
      if (this.symbols.size > 0) {
        await this.connection.invoke('SubscribeQuotes', [...this.symbols]);
      }
      for (const login of this.watched.keys()) {
        await this.watch(login);
      }
      if (this.staffAlerts) await this.connection.invoke('WatchStaffAlerts');
    } catch {
      // the connection dropped again while restoring: the next reconnect starts over
    } finally {
      this.resubscribing = false;
    }
    for (const [login, reason] of [...this.pendingResync]) {
      this.pendingResync.delete(login);
      this.emit('resync', { login, reason });
    }
  }

  private async watch(login: number): Promise<void> {
    const entry = this.watched.get(login);
    if (!entry) return;
    const baseline = await this.connection.invoke<number>('WatchAccount', login, entry.lastSeq);
    if (entry.lastSeq === null) {
      entry.lastSeq = baseline; // nothing to replay yet: from here on every event is delivered live
    }
    this.watchWaiters.get(login)?.forEach((resolve) => resolve());
    this.watchWaiters.delete(login);
  }

  private onEvent(message: HubEvent): void {
    const entry = this.watched.get(message.event.login);
    if (!entry && this.staffAlerts) {
      this.emit('staffAlert', message); // an alert about an account this connection does not watch
      return;
    }
    if (entry) {
      if (entry.lastSeq !== null && message.seq <= entry.lastSeq) {
        return; // a replay that overlaps what we already have
      }
      entry.lastSeq = message.seq;
    }
    this.emit('event', message);
  }

  private async onResync(resync: Resync): Promise<void> {
    if (resync.reason === 'session_revoked') {
      // Signed out elsewhere or by an admin: renewing fails, which ends the session; do not reconnect forever.
      const rejected = await this.tokens.getToken();
      if (!(await this.tokens.refresh(rejected)) && this.tokens.session.status !== 'authenticated') {
        await this.stop();
        return;
      }
    }
    if (resync.login === null) {
      // The server is closing this connection (overflow, expired token): every account must be re-baselined after we reconnect.
      this.watched.forEach((entry) => (entry.lastSeq = null));
    } else {
      const entry = this.watched.get(resync.login);
      if (entry) entry.lastSeq = null; // baseline is re-read from the WatchAccount answer
    }
    if (this.resubscribing || this.current !== 'connected' || resync.login === null) {
      this.pendingResync.set(resync.login, resync.reason); // reported once the subscriptions are back
    } else {
      this.pendingResync.set(resync.login, resync.reason);
      queueMicrotask(() => void this.flushResync());
    }
  }

  private async flushResync(): Promise<void> {
    if (this.resubscribing) return;
    for (const [login, reason] of [...this.pendingResync]) {
      if (login !== null) {
        await this.watch(login).catch(() => undefined);
      }
      this.pendingResync.delete(login);
      this.emit('resync', { login, reason });
    }
  }

  private nextDelay(context: RetryContext): number | null {
    const status = this.tokens.session.status;
    if (this.stopped || status === 'anonymous' || status === 'expired') return null; // give up: nobody is signed in
    return this.delayFor(context.previousRetryCount);
  }

  private delayFor(attempt: number): number {
    const base = this.backoff[Math.min(attempt, this.backoff.length - 1)] ?? 15_000;
    return Math.round(base * (0.8 + this.random() * 0.4));
  }

  private setState(state: HubState): void {
    if (this.current === state) return;
    this.current = state;
    this.emit('state', state);
  }

  private emit<K extends keyof HubHandlers>(name: K, ...args: Parameters<HubHandlers[K]>): void {
    for (const handler of this.handlers[name]) {
      (handler as (...a: unknown[]) => void)(...args);
    }
  }
}
